// AGC: v5.5.0 - Splash screen component (logo.gif animation)
// AGC: Used for both initial app load (quick) and home page refresh (full duration)
import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

interface SplashProps {
  onComplete: () => void
  duration?: number // AGC: Duration in milliseconds (default: 4000ms for full splash, 300ms for quick load)
  fullScreen?: boolean // AGC: If true, covers full screen (for app initialization). If false, respects nav bars (for home page)
}

export const Splash: React.FC<SplashProps> = ({ onComplete, duration = 4000, fullScreen = false }) => {
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 375)
  const [isFading, setIsFading] = useState(false)

  // AGC: Track viewport width for full-width splash
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const updateWidth = () => setViewportWidth(window.innerWidth)
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  // AGC: Remove body margins/padding when splash is shown
  useEffect(() => {
    if (typeof document === 'undefined') return
    
    const originalBodyMargin = document.body.style.margin
    const originalBodyPadding = document.body.style.padding
    const originalHtmlMargin = document.documentElement.style.margin
    const originalHtmlPadding = document.documentElement.style.padding
    
    document.body.style.margin = '0'
    document.body.style.padding = '0'
    document.documentElement.style.margin = '0'
    document.documentElement.style.padding = '0'
    
    return () => {
      document.body.style.margin = originalBodyMargin
      document.body.style.padding = originalBodyPadding
      document.documentElement.style.margin = originalHtmlMargin
      document.documentElement.style.padding = originalHtmlPadding
    }
  }, [])

  // AGC: Splash duration - configurable, then fade out and call onComplete
  useEffect(() => {
    // AGC: Start fade 300ms before completion
    const fadeStart = Math.max(0, duration - 300)
    const fadeTimer = setTimeout(() => setIsFading(true), fadeStart)
    const completeTimer = setTimeout(() => {
      onComplete()
    }, duration)

    return () => {
      clearTimeout(fadeTimer)
      clearTimeout(completeTimer)
    }
  }, [onComplete, duration])

  // AGC: Use full screen for app initialization, or respect nav bars for home page
  const containerStyle = fullScreen
    ? {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        width: `${viewportWidth}px`,
        height: '100vh',
      }
    : {
        top: '72px',
        bottom: '88px',
        left: 0,
        right: 0,
        width: `${viewportWidth}px`,
        height: 'calc(100vh - 160px)',
      }

  return createPortal(
    <div 
      className="fixed bg-black flex items-center justify-center z-30" 
      style={{ 
        ...containerStyle,
        margin: 0,
        padding: 0,
        maxWidth: 'none',
        overflow: 'hidden',
        boxSizing: 'border-box',
        position: 'fixed',
        opacity: isFading ? 0 : 1,
        transition: 'opacity 0.3s ease-out',
        visibility: isFading ? 'hidden' : 'visible',
        zIndex: 30 // AGC: Lower z-index so TopNav buttons (z-50) and BottomNav (z-40) appear on top
      }}
    >
      <img 
        src={`${import.meta.env.BASE_URL}logo.gif`} 
        alt="Origami Battles Loading" 
        className="w-72 h-72 sm:w-96 sm:h-96 object-contain"
        style={{
          animation: 'none',
          display: 'block'
        }}
      />
    </div>,
    document.body
  )
}

